import { useState } from 'react'
import { Arc } from '@visx/shape'
import type { ChartCanvasProps, ScoreRingChartData } from './chartTypes'
import { TooltipCard } from './TooltipCard'

const toneColor = (state: ScoreRingChartData['state']) => {
  if (state === 'CRITICAL') return 'var(--chart-4)'
  if (state === 'WATCH') return 'var(--chart-3)'
  return 'var(--chart-2)'
}

type Active = { label: string; value: number; color: string; x: number; y: number } | null

export const ScoreRingChart = ({ data, width, height }: ChartCanvasProps<ScoreRingChartData>) => {
  const [active, setActive] = useState<Active>(null)
  const size = Math.min(width, height)
  const outerRadius = Math.max(28, size / 2 - 12)
  const innerRadius = Math.max(14, outerRadius - 18)
  const scoreAngle = (Math.max(0, Math.min(100, data.score)) / 100) * Math.PI * 2
  const accent = toneColor(data.state)
  const halfSize = size / 2

  return (
    <div className="analysisChartCanvas analysisChartCanvas--center">
      <svg className="analysisChartSvg analysisChartSvg--ring" viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Workspace health ${data.score} out of 100`}>
        <title>{`Workspace health ${data.score}`}</title>
        <g transform={`translate(${halfSize} ${halfSize})`}>
          <Arc startAngle={0} endAngle={Math.PI * 2} innerRadius={innerRadius} outerRadius={outerRadius} fill="var(--chart-grid)" />
          <Arc startAngle={0} endAngle={scoreAngle} innerRadius={innerRadius} outerRadius={outerRadius} cornerRadius={8} fill={accent} />
          {data.subScores.map((bar, index) => {
            const radius = innerRadius - 14 - index * 9
            if (radius <= 4) return null
            const color = index % 2 ? 'var(--chart-2)' : 'var(--chart-6)'
            const dashLength = (bar.value / 100) * 2 * Math.PI * radius
            const cx = 0
            const cy = -radius
            return (
              <g key={bar.label}>
                <circle
                  r={radius}
                  cx="0"
                  cy="0"
                  fill="none"
                  stroke="var(--chart-grid)"
                  strokeWidth="4"
                />
                <circle
                  r={radius}
                  cx="0"
                  cy="0"
                  fill="none"
                  stroke={color}
                  strokeWidth="4"
                  strokeDasharray={`${dashLength} ${2 * Math.PI * radius}`}
                  transform="rotate(-90)"
                  tabIndex={0}
                  role="button"
                  aria-label={`${bar.label}: ${bar.value}%`}
                  onPointerEnter={() => setActive({ label: bar.label, value: bar.value, color, x: halfSize + cx, y: halfSize + cy })}
                  onPointerLeave={() => setActive(null)}
                  onFocus={() => setActive({ label: bar.label, value: bar.value, color, x: halfSize + cx, y: halfSize + cy })}
                  onBlur={() => setActive(null)}
                />
              </g>
            )
          })}
        </g>
        <text className="analysisChartRingValue" x={halfSize} y={halfSize + 6} textAnchor="middle">{data.score}</text>
        <text className="analysisChartRingLabel" x={halfSize} y={halfSize + 26} textAnchor="middle">{data.state}</text>
      </svg>
      {active && (
        <TooltipCard
          left={active.x}
          top={active.y}
          accent={active.color}
          title={active.label}
          value={`${Math.round(active.value)}%`}
        />
      )}
    </div>
  )
}
